import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from '../utils/axios';

/**
 * Load activity types from API.
 * Falls back to empty list on failure so callers can keep legacy hardcoded maps
 * until the backend route is deployed.
 */
export default function useActivityTypes({ homeOnly = false, admin = false } = {}) {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTypes = useCallback(async () => {
    try {
      setLoading(true);
      const url = admin
        ? '/api/activity-types/all'
        : homeOnly
          ? '/api/activity-types?home=true'
          : '/api/activity-types';
      const { data } = await axios.get(url);
      setTypes(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError(err);
      setTypes([]);
    } finally {
      setLoading(false);
    }
  }, [admin, homeOnly]);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  const byName = useMemo(() => {
    const map = {};
    types.forEach((t) => {
      map[t.name] = t;
    });
    return map;
  }, [types]);

  const colors = useMemo(() => {
    const map = {};
    types.forEach((t) => {
      map[t.name] = t.color;
    });
    return map;
  }, [types]);

  const displayNames = useMemo(() => {
    const map = {};
    types.forEach((t) => {
      map[t.name] = t.displayName || t.name;
    });
    return map;
  }, [types]);

  const names = useMemo(() => types.map((t) => t.name), [types]);

  return {
    types,
    names,
    byName,
    colors,
    displayNames,
    loading,
    error,
    refresh: fetchTypes,
    ready: !loading && types.length > 0
  };
}
